import type {
    AdjustItems,
    AdjustedValuesStatus,
    AnyAdjustValues,
    RollToActionValues,
    RollToActions,
    RollToErrors,
    RollToOptionsState,
    RollToTabState,
    StateTree,
    TargetStatus
} from '@makemydeal/dr-dash-types';
import { getAccessoriesTotalAmount } from '../selectors/accessoriesSelectors';
import { getVppSummary } from './vehicleProtectionSelectors';

import type { RollToOption, RollToOptions, RollToOptionsOverrides } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { createSelector } from 'reselect';
import { ROLL_TO_ADJUSTMENT_TYPE_BY_TAB, ROLL_TO_OPTIONS_ROOT } from '../constants';
import {
    getBuyRateOverrideByOfferTypeAndTerm,
    getCashDueFromCustomer,
    getCurrentOfferType,
    getFirstMonthPayment,
    isOnePayLease
} from './offerRedux';
import { ROLL_TO_OPTIONS_ADJUSTMENT_TYPE, ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import { getBalanceDueAmountByOffer, getProfitDetail } from './offerSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const getRollToOptions = (state: StateTree): RollToOptionsState => {
    return state[ROLL_TO_OPTIONS_ROOT] || ({} as RollToOptionsState);
};

export const getTabState = (state: StateTree, tabOverride?: ROLL_TO_OPTIONS_LABELS): RollToTabState => {
    const activeTab = tabOverride || getActiveTab(state);

    if (!activeTab) {
        return {};
    }

    return getRollToOptions(state).tabStateByLabel?.[activeTab] || {};
};

export const getRollTargetPayment = (state: StateTree): number => {
    return getTabState(state, ROLL_TO_OPTIONS_LABELS.PAYMENT).targetPayment || 0;
};

export const getRollToItems = (state: StateTree): RollToActions | null | undefined => {
    const activeTab = getActiveTab(state);
    const rollToOptions = getRollToOptions(state);
    const baseRollToItems = activeTab ? getTabState(state).rollToItems : rollToOptions.rollToItems;

    if (!baseRollToItems) {
        return undefined;
    }

    const current = baseRollToItems.current || {};
    const tabAdjust = baseRollToItems.adjust || {};

    return {
        ...baseRollToItems,
        adjust: {
            ...current,
            ...tabAdjust,
            actionValue: tabAdjust.actionValue ?? current.actionValue,
            adjustmentType: current.adjustmentType ?? tabAdjust.adjustmentType
        }
    };
};

export const getTargetBalanceDue = (state: StateTree): number => {
    return getTabState(state, ROLL_TO_OPTIONS_LABELS.BALANCE_DUE).targetBalanceDue || 0;
};

export const getTargetCashDue = (state: StateTree): number => {
    return getTabState(state, ROLL_TO_OPTIONS_LABELS.CASH_DUE).targetCashDue || 0;
};

export const isRollToCashDueApplicable = (state: StateTree): boolean =>
    featureToggleSelectors.isGetRollToCashDueEnabled(state) && getCurrentOfferType(state) === LEASE && isOnePayLease(state);

export const getBalanceDue = (state: StateTree): number => {
    return getTabState(state).balanceDue || 0;
};

export const getPayment = (state: StateTree): number => {
    return getTabState(state).payment || 0;
};

export const getRollTargetProfit = (state: StateTree): number => {
    return getTabState(state, ROLL_TO_OPTIONS_LABELS.PROFIT).targetProfit || 0;
};

export const getRollToPaymentSlideOutExpanded = (state: StateTree): boolean => {
    const rollToOptions = getRollToOptions(state);
    return rollToOptions.rollToSlideOutExpanded || false;
};

export const getActiveTab = (state: StateTree): ROLL_TO_OPTIONS_LABELS | undefined => {
    return getRollToOptions(state).activeTab;
};

export const getVppAndProtectionTotal = (state: StateTree): number => {
    const protectionTotal = getVppSummary(state).totalProductPrice || 0;
    // TODO: When accessories is implemented, make sure the total includes only capitalized accessories for lease
    const accessoriesTotal = getAccessoriesTotalAmount(state);
    return protectionTotal + accessoriesTotal;
};
export const getRollToUnsavedChanges = (state: StateTree): boolean => {
    const rollToOptions = getRollToOptions(state);
    return rollToOptions.hasUnsavedChanges || false;
};

export const hasRollToOptionsItems = (state: StateTree): boolean => {
    return Boolean(getRollToItems(state));
};

export const getRollToOptionsItems = (state: StateTree): RollToActions => {
    return getRollToItems(state) || {};
};

export const getAdjustedValues = (state: StateTree): RollToActionValues => {
    const rollToOptionsItems = getRollToOptionsItems(state);
    return rollToOptionsItems.adjust || {};
};

export const getCurrentValues = (state: StateTree): RollToActionValues => {
    const rollToOptionsItems = getRollToOptionsItems(state);
    return rollToOptionsItems.current || {};
};

export const isCalculating = (state: StateTree): boolean => {
    const rollToOptions = getRollToOptions(state);
    return Boolean(rollToOptions.isCalculating);
};

export const getRollToErrors = (state: StateTree): RollToErrors => {
    return getTabState(state).errors || [];
};

export const hasRollToErrors = (state: StateTree): boolean => {
    const errors = getRollToErrors(state);
    return Boolean(errors.length);
};

export const getTargetStatus = (state: StateTree): TargetStatus | undefined => {
    const rollToOptions = getRollToOptions(state);
    return rollToOptions.targetStatus;
};

export const getAdjustedStatus = createSelector(
    getAdjustedValues,
    getCurrentValues,
    (currentValues, adjustedValues): AdjustedValuesStatus => {
        const status: AdjustedValuesStatus = {};

        status.term = currentValues.term !== adjustedValues.term;
        status.rate = currentValues.rate !== adjustedValues.rate;
        status.trade = currentValues.trade !== adjustedValues.trade;
        status.downPayment = currentValues.downPayment !== adjustedValues.downPayment;
        status.sellingPrice = currentValues.sellingPrice !== adjustedValues.sellingPrice;
        status.balloonAmount = currentValues.balloonAmount !== adjustedValues.balloonAmount;

        return status;
    }
);

export const getAdjustedBuyRateOption = (state: StateTree, sellRate: RollToOption, term?: number): RollToOption => {
    const offerType = getCurrentOfferType(state);
    const buyRate = getBuyRateOverrideByOfferTypeAndTerm(state, offerType, term, true);
    const sellRateValue = sellRate?.value ?? 0;

    const buyRateOption: RollToOption = { adjusted: false, value: buyRate };

    if (!buyRate || buyRate > sellRateValue) {
        if (buyRate != null) {
            buyRateOption.value = buyRate > sellRateValue ? sellRateValue : buyRate;
        } else {
            buyRateOption.value = undefined;
        }
        buyRateOption.adjusted = Boolean(sellRate?.adjusted);
    }
    return buyRateOption;
};

export const hasAdjustedValues = (state: StateTree): boolean => {
    const rollToOptions = getRollToOptions(state);

    if (!featureToggleSelectors.isRollToTabPersistenceEnabled(state)) {
        return hasAdjustedValuesFromRollToItems(rollToOptions.rollToItems);
    }

    // When toggle is ON, only check the active/current tab for adjustments
    const activeTab = getActiveTab(state);
    if (!activeTab) {
        return false;
    }

    const tabState = rollToOptions.tabStateByLabel?.[activeTab];
    return hasAdjustedValuesFromRollToItems(tabState?.rollToItems);
};

const hasTradeInsValueDiff = (
    currentTradeIns?: Array<{ value?: number; callerItemId?: number | string }> | null,
    adjustedTradeIns?: Array<{ value?: number; callerItemId?: number | string }> | null
): boolean => {
    const currentItems = currentTradeIns || [];
    const adjustedItems = adjustedTradeIns || [];

    if (currentItems.length !== adjustedItems.length) {
        return true;
    }

    return currentItems.some((currentItem, index) => {
        const adjustedItem = adjustedItems[index];
        return currentItem?.value !== adjustedItem?.value || currentItem?.callerItemId !== adjustedItem?.callerItemId;
    });
};

const hasAdjustedValuesFromRollToItems = (rollToItems?: RollToActions | null): boolean => {
    const current = rollToItems?.current || {};
    const adjust = rollToItems?.adjust || {};
    const found = Object.keys(current).find((key: string) => {
        const item = key as AdjustItems;
        if (item === 'tradeIns') {
            return hasTradeInsValueDiff(
                current[item] as Array<{ value?: number; callerItemId?: number | string }> | undefined,
                adjust[item] as Array<{ value?: number; callerItemId?: number | string }> | undefined
            );
        }
        return current[item] !== adjust[item];
    });

    return Boolean(found);
};

export const adjustOptions = (
    adjustedOptions: RollToOptions,
    adjusted: AnyAdjustValues,
    currentValues?: RollToActionValues
): RollToOptions => {
    const newAdjustedOptions = { ...adjustedOptions };
    Object.keys(newAdjustedOptions)
        .filter((key: string) => {
            const item = key as AdjustItems;
            return item === 'tradeIns' || (adjusted[item] != null && adjusted[item] !== adjustedOptions[item]?.value);
        })
        .forEach((key: string) => {
            const item = key as AdjustItems;
            if (item === 'tradeIns') {
                const adjustedTradeIns = [...(adjusted.tradeIns || [])];
                const adjustedOptionTradeIns = [...(newAdjustedOptions.tradeIns || [])];
                const currentTradeIns = [...(currentValues?.tradeIns || [])];
                adjustedOptionTradeIns.forEach((option, index) => {
                    const callerItemId = option?.callerItemId ?? adjustedTradeIns[index]?.callerItemId;
                    const matchingAdjusted =
                        callerItemId != null
                            ? adjustedTradeIns.find((t) => t?.callerItemId === callerItemId) ?? adjustedTradeIns[index]
                            : adjustedTradeIns[index];
                    const adjustedValue = matchingAdjusted?.value ?? option?.value;
                    const matchingCurrentByCallerId = currentTradeIns.find((trade) => trade?.callerItemId === callerItemId);
                    const currentValue = matchingCurrentByCallerId?.value ?? currentTradeIns[index]?.value ?? option?.value;
                    const isAdjusted = currentValue !== adjustedValue;

                    adjustedOptionTradeIns[index] = {
                        adjusted: isAdjusted,
                        value: adjustedValue,
                        callerItemId
                    };
                });
                newAdjustedOptions.tradeIns = adjustedOptionTradeIns;
            } else {
                newAdjustedOptions[item] = {
                    adjusted: newAdjustedOptions[item]?.value !== adjusted[item],
                    value: adjusted[item]
                };
            }
        });

    return newAdjustedOptions;
};

export const getAdjustedOptions = createSelector(
    getAdjustedStatus,
    getAdjustedValues,
    getCurrentValues,
    (status, values, current): RollToOptions => {
        const options: RollToOptions = {
            term: { adjusted: status.term, value: values.term },
            rate: { adjusted: status.rate, value: values.rate },
            trade: { adjusted: status.trade, value: values.trade },
            downPayment: { adjusted: status.downPayment, value: values.downPayment },
            sellingPrice: { adjusted: status.sellingPrice, value: values.sellingPrice },
            balloonAmount: { adjusted: status.balloonAmount, value: values.balloonAmount }
        };

        const tradeIns = (values.tradeIns || []).map((t, index) => ({
            value: t.value,
            adjusted: (current.tradeIns || [])[index]?.value !== t.value,
            callerItemId: t.callerItemId
        }));
        return { ...options, tradeIns };
    }
);

export const getAdjustedStatusOptions = (state: StateTree, adjusted?: AnyAdjustValues): RollToOptions => {
    let adjustedOptions = getAdjustedOptions(state);

    if (adjusted != null) {
        const currentValues = getCurrentValues(state);
        adjustedOptions = adjustOptions(adjustedOptions, adjusted, currentValues);
    }

    adjustedOptions.buyRate = getAdjustedBuyRateOption(state, adjustedOptions.rate, adjustedOptions.term?.value);

    return adjustedOptions;
};

/**
 * Each entry wraps its selector in an arrow rather than referencing it directly so the lookup is resolved
 * when the value is read, not when this module is evaluated — otherwise test spies on the underlying
 * selectors would never be seen.
 */
const CURRENT_VALUE_SELECTOR_BY_ADJUSTMENT_TYPE: Record<ROLL_TO_OPTIONS_ADJUSTMENT_TYPE, (state: StateTree) => number> = {
    [ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.MONTHLY_PAYMENT]: (state) => getFirstMonthPayment(state),
    [ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.BALANCE_DUE]: (state) => getBalanceDueAmountByOffer(state),
    [ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.CASH_DUE]: (state) => getCashDueFromCustomer(state),
    [ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.PROFIT]: (state) => getProfitDetail(state)
};

/**
 * Current value of the amount a roll targeted, used to report what the applied roll actually landed on.
 * An unrecognized adjustment type falls back to profit — the behavior this lookup replaced.
 */
export const getCurrentValueByAdjustmentType = (state: StateTree, adjustmentType?: string): number =>
    (
        CURRENT_VALUE_SELECTOR_BY_ADJUSTMENT_TYPE[adjustmentType as ROLL_TO_OPTIONS_ADJUSTMENT_TYPE] ??
        CURRENT_VALUE_SELECTOR_BY_ADJUSTMENT_TYPE[ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.PROFIT]
    )(state);

export const getRollToAdjustmentTarget = (state: StateTree): any => {
    const rollToItems = getRollToItems(state);
    const tab = featureToggleSelectors.isRollToTabPersistenceEnabled(state)
        ? getActiveTab(state)
        : rollToItems?.current?.adjustmentType;

    if (!tab) {
        return {};
    }

    const name = ROLL_TO_ADJUSTMENT_TYPE_BY_TAB[tab as ROLL_TO_OPTIONS_LABELS] ?? ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.PROFIT;

    return { name, value: getCurrentValueByAdjustmentType(state, name) };
};

export const getApplyRollToPayload = createSelector(
    hasAdjustedValues,
    getRollToAdjustmentTarget,
    getAdjustedStatusOptions,
    (hasAdjustments, target, options): RollToOptionsOverrides => {
        return {
            target,
            options,
            hasAdjustments
        };
    }
);
